// MyPaginationComponent.jsx
import React from "react";
import { styled } from "@mui/material/styles";
import MuiPagination from "@mui/material/Pagination";
import MuiPaginationItem from "@mui/material/PaginationItem";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";

// Copying the pagination style from TransactionPage
const CustomPagination = styled(MuiPagination)(({ theme }) => ({
  "&.MuiPagination-root": {
    margin: theme.spacing(1),
    backgroundColor: "#fff", // Entire pagination bar is white
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  "& .MuiPaginationItem-root": {
    minWidth: 36,
    height: 36,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0, 0.5),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontWeight: 500,
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  "& .MuiPaginationItem-ellipsis": {
    color: theme.palette.text.secondary,
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: "#fff",
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    fontWeight: 600,
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    opacity: 0.6,
  },
}));

function MyPaginationComponent({
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) {
  // Calculate total pages from the total record count
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  // Calculate displayed range (e.g., "Showing 1–10 of 156 records")
  const startIndex = (page - 1) * rowsPerPage + 1;
  const endIndex = Math.min(page * rowsPerPage, totalCount);

  return (
    <Box
      sx={{
        p: 2,
        mt: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        backgroundColor: "#fff",
        borderTop: "1px solid #ddd",
      }}
    >
      {/* Display current record range */}
      <Typography variant="body2" color="text.secondary">
        {`Showing ${startIndex}-${endIndex} of ${totalCount} records`}
      </Typography>

      {/* Rows dropdown and pagination controls */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <FormControl variant="outlined" size="small" sx={{ width: 80 }}>
          <InputLabel>Rows</InputLabel>
          <Select
            label="Rows"
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(e.target.value)}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
          </Select>
        </FormControl>
        <CustomPagination
          page={page} // expects a 1-based page number
          count={totalPages}
          onChange={(event, newPage) => onPageChange(newPage)}
          siblingCount={1}
          boundaryCount={1}
          renderItem={(item) => {
            if (item.type === "previous") {
              return (
                <MuiPaginationItem {...item} disableRipple>
                  &lt;
                </MuiPaginationItem>
              );
            }
            if (item.type === "next") {
              return (
                <MuiPaginationItem {...item} disableRipple>
                  &gt;
                </MuiPaginationItem>
              );
            }
            return <MuiPaginationItem {...item} disableRipple />;
          }}
        />
      </Box>
    </Box>
  );
}

export default MyPaginationComponent;
